import { useEffect, useState } from 'react';
import {
  Empty,
  HeldBeat,
  Link,
  Page,
  PageBackdrop,
  Receipt,
  soundedPointerHandlers,
  StoreHero,
  ui,
  VoltFlash,
  type CapsuleTitle,
  type ReceiptRow,
} from '../ui';
import { formatDate, formatMoney } from '@goodbuild/rules/format';
import type { OrderSummary, OwnedTitle } from '@api/views';
import { artUrlFor, useStorePage } from '../catalogue';
import { useApi } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { playSound } from '../ui/sound';
import { column, Loading } from './shared';
import { useDocumentTitle } from '../title';

const MINUTES_AN_ORDER_STAYS_NEW = 30;
const CONFIRMATION_POLL_MS = 2000;
const CONFIRMATION_POLL_LIMIT = 12;
const ACTIVATION_POLL_MS = 3000;
const HELD_BEAT_BEFORE_THE_LINE_MS = 420;
const HELD_BEAT_BEFORE_THE_RECEIPT_MS = 1100;

function placedWithinTheLast(placedAt: Date | string, minutes: number): boolean {
  const placedAtMs = new Date(placedAt).getTime();
  if (Number.isNaN(placedAtMs)) return false;
  return Date.now() - placedAtMs < minutes * 60_000;
}

function receiptRowsFor(summary: OrderSummary): ReceiptRow[] {
  const { order, lines } = summary;

  return [
    { label: 'Order', value: order.id },
    { label: 'Bought', value: formatDate(order.placedAt) },
    { label: 'Games', value: String(lines.length) },
    { label: 'Tax', value: formatMoney(order.currency, order.taxMinor) },
    { label: 'Paid', value: formatMoney(order.currency, order.pricePaidMinor) },
  ];
}

export function BoughtRoute(): React.ReactElement {
  useDocumentTitle('It is yours');

  const query = useApi<{ orders: OrderSummary[] }>(['orders'], '/me/orders');
  const { refetch } = query;

  const newestOrder = query.data?.orders[0] ?? null;
  const orderIsConfirmed =
    newestOrder !== null &&
    placedWithinTheLast(newestOrder.order.placedAt, MINUTES_AN_ORDER_STAYS_NEW);

  const [pollsSpent, setPollsSpent] = useState(0);
  const stillWaitingOnTheWebhook = !orderIsConfirmed && pollsSpent < CONFIRMATION_POLL_LIMIT;

  useEffect(() => {
    if (!stillWaitingOnTheWebhook) return;

    const timer = window.setTimeout(() => {
      setPollsSpent((spent) => spent + 1);
      void refetch();
    }, CONFIRMATION_POLL_MS);

    return () => window.clearTimeout(timer);
  }, [stillWaitingOnTheWebhook, pollsSpent, refetch]);

  useEffect(() => {
    if (orderIsConfirmed) playSound('purchaseStamped');
  }, [orderIsConfirmed]);

  const firstLine = newestOrder?.lines[0] ?? null;
  const boughtGame = useStorePage(firstLine?.slug ?? '');

  const boughtSlug = firstLine?.slug ?? '';
  const ownedQuery = useApi<OwnedTitle>(
    ['library', boughtSlug],
    `/me/library/${encodeURIComponent(boughtSlug)}`,
    { enabled: boughtSlug !== '' && orderIsConfirmed },
  );
  const stillActivating = ownedQuery.data?.awaitingActivation === true;
  const cache = useQueryClient();

  useEffect(() => {
    if (!stillActivating) return;

    const timer = window.setInterval(() => {
      void cache.invalidateQueries({ queryKey: ['library', boughtSlug] });
    }, ACTIVATION_POLL_MS);

    return () => window.clearInterval(timer);
  }, [stillActivating, boughtSlug, cache]);
  const boughtListing = boughtGame.data?.listing;
  const boughtArt = boughtGame.data?.art;

  const hero: CapsuleTitle | null = boughtListing
    ? {
        slug: boughtListing.slug,
        name: boughtListing.name,
        studio: boughtListing.developerName,
        price: null,
        artUrl: artUrlFor(boughtArt, boughtListing.id, 'capsule_main'),
        heroArtUrl: artUrlFor(boughtArt, boughtListing.id, 'library_hero'),
        pageBackgroundUrl: artUrlFor(boughtArt, boughtListing.id, 'page_background'),
        logoUrl: artUrlFor(boughtArt, boughtListing.id, 'logo'),
        liveViewers: null,
      }
    : null;

  if (query.isPending) {
    return (
      <Page site="store">
        <Loading label="Finding your order" />
      </Page>
    );
  }

  if (!orderIsConfirmed) {
    return (
      <Page site="store">
        {stillWaitingOnTheWebhook ? (
          <Loading label="Confirming your purchase" />
        ) : (
          <Empty>
            We have not been told about that payment yet. It usually lands in a minute or two —{' '}
            <Link href={'/orders'}>your orders</Link> will show it, and your email receipt comes
            from Creem.
          </Empty>
        )}
      </Page>
    );
  }

  if (!newestOrder) return <Empty>Nothing to show yet.</Empty>;

  return (
    <Page
      site="store"
      backdrop={<PageBackdrop imageUrl={hero?.pageBackgroundUrl ?? null} />}
      bleed={hero ? <StoreHero title={hero} tall headingLevel="h1" /> : undefined}
    >
      <VoltFlash trigger={newestOrder.order.id} />

      <div style={column(6)}>
        <HeldBeat afterMs={HELD_BEAT_BEFORE_THE_LINE_MS}>
          <ItIsYours lines={newestOrder.lines} />
        </HeldBeat>

        <HeldBeat afterMs={HELD_BEAT_BEFORE_THE_RECEIPT_MS}>
          <div style={column(6)}>
            <Receipt rows={receiptRowsFor(newestOrder)} />

            {stillActivating ? (
              <div style={column(3)}>
                <Loading label="Activating your copy" />
                <p className="gb-body" style={{ margin: 0 }}>
                  We are activating it with our payment provider before you install. It takes a few
                  seconds and this page updates itself.
                </p>
              </div>
            ) : null}

            <div className={ui.actions ?? ''}>
              <Link href={'/download'} className={ui.button ?? ''} {...soundedPointerHandlers}>
                Get the launcher
              </Link>
              <Link
                href={'/library'}
                className={ui.buttonSecondary ?? ''}
                {...soundedPointerHandlers}
              >
                Your library
              </Link>
              <Link href={'/'} className={ui.buttonSecondary ?? ''} {...soundedPointerHandlers}>
                Back to the games
              </Link>
            </div>

            <p className="gb-body">
              Creem took the payment and sent the receipt. You can ask for your money back from{' '}
              <Link href={'/orders'}>your orders</Link>.
            </p>
          </div>
        </HeldBeat>
      </div>
    </Page>
  );
}

function ItIsYours({ lines }: { lines: OrderSummary['lines'] }): React.ReactElement {
  useEffect(() => {
    playSound('ownershipGranted');
  }, []);

  return (
    <div style={column(4)}>
      <h2 className="gb-display" style={{ margin: 0 }}>
        {lines.length > 1 ? "They're yours." : "It's yours."}
      </h2>
      <p className="gb-lead" style={{ margin: 0 }}>
        {lines.map((line) => line.name).join(', ')}
        {lines.length > 1 ? ' are in your library.' : ' is in your library.'}
      </p>
    </div>
  );
}
